import { memo, useEffect, useLayoutEffect, useState } from 'react';
import * as THREE from 'three';

import {
  createBackdropGradientTextures,
  disposeBackdropGradientTextures,
  updateBackdropGradientTextures,
} from './backdropGradientTextures';

export default function Backdrop({
  hexColor = '#42aad7',
  useBackgroundGradient,
  radius = 500,
}: {
  hexColor?: string;
  useBackgroundGradient?: boolean;
  radius?: number;
}) {
  return (
    <>
      {useBackgroundGradient ? (
        <BackdropGradient hexColor={hexColor} radius={radius} />
      ) : (
        <>
          <BackdropSolid hexColor={hexColor} planePosition="top" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="bottom" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="front" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="back" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="left" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="right" radius={radius} />
        </>
      )}
    </>
  );
}

function BackdropSolid({
  hexColor,
  planePosition,
  radius = 500,
}: {
  hexColor: string;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
  radius?: number;
}) {
  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  const planeSize = radius * 4;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, radius, 0);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -radius, 0);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, radius);
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-radius, 0, 0);
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(radius, 0, 0);
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
  }
  return (
    <mesh
      rotation={rot}
      position={pos}
      receiveShadow={false}
      castShadow={false}
    >
      <planeGeometry attach="geometry" args={[planeSize, planeSize]} />
      <meshBasicMaterial
        attach="material"
        color={new THREE.Color(hexColor)}
        side={THREE.FrontSide}
        toneMapped={false}
        depthWrite={true}
        fog={false}
      />
    </mesh>
  );
}

function BackdropGradient({
  hexColor,
  radius = 500,
}: {
  hexColor: string;
  radius?: number;
}) {
  const [textures] = useState(() => createBackdropGradientTextures(hexColor));

  useLayoutEffect(() => {
    updateBackdropGradientTextures(textures, hexColor);
  }, [hexColor, textures]);

  useEffect(
    () => () => {
      disposeBackdropGradientTextures(textures);
    },
    [textures],
  );

  return (
    <>
      <BackdropGradientPlane
        map={textures.top.texture}
        planePosition="top"
        radius={radius}
      />
      <BackdropGradientPlane
        map={textures.bottom.texture}
        planePosition="bottom"
        radius={radius}
      />
      <BackdropGradientPlane
        map={textures.wall.texture}
        planePosition="front"
        radius={radius}
      />
      <BackdropGradientPlane
        map={textures.wall.texture}
        planePosition="back"
        radius={radius}
      />
      <BackdropGradientPlane
        map={textures.wall.texture}
        planePosition="left"
        radius={radius}
      />
      <BackdropGradientPlane
        map={textures.wall.texture}
        planePosition="right"
        radius={radius}
      />
    </>
  );
}

const BackdropGradientPlane = memo(function BackdropGradientPlane({
  map,
  planePosition,
  radius,
}: {
  map: THREE.Texture;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
  radius: number;
}) {
  const planeSize = radius * 4;

  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, radius, 0);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -radius, 0);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, radius);
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-radius, 0, 0);
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(radius, 0, 0);
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -1000);
  }
  return (
    <mesh rotation={rot} position={pos}>
      <planeGeometry attach="geometry" args={[planeSize, planeSize]} />
      <meshBasicMaterial
        attach="material"
        map={map}
        side={THREE.FrontSide}
        depthWrite={true}
        fog={false}
      />
    </mesh>
  );
});
